
import { computed, defineComponent, onMounted, ref, watchEffect, watch, nextTick, toRaw } from 'vue';
import { cloneDeep, isEqual } from 'lodash';
import { Client, Material, Truck } from '@/types/app';
import { Location, NumberWithMetric } from '@/types/shared';
import { useGoogleMaps } from '@/services';
import { useStore } from '@/store';
import {
  createEnumerableEnum,
  getRouteDuration,
  getVendorDirectionsWithinSearchRadius,
  calculateRouteCostWithTruck,
  useModalState,
  useSharedInternalState,
  useSelectSubmittedClient,
  useIsLoadingWithDelay,
  geoPointToLatLng
} from '@/utils';
import { emptyValues, formDefaults } from '@/constants';
import { Option, Route } from '@/types';
import { DistanceUnits, VolumeAndWeightUnits } from '@/enums';
import router from '@/router';
import LocationInput from '@/components/form-controls/LocationInput.vue';
import MaterialsSelect from '@/components/form-controls/MaterialsSelect.vue';
import NumberInput from '@/components/form-controls/base/NumberInput.vue';
import CheckboxInput from '@/components/form-controls/base/CheckboxInput.vue';
import NumberWithMetricInput from '@/components/form-controls/NumberWithMetricInput.vue';
import FilterableSelect from '@/components/form-controls/base/FilterableSelect.vue';
import VendorListing from '@/components/VendorListing.vue';
import MaterialIcon from '@/components/MaterialIcon.vue';
import ClientsSelect from '@/components/form-controls/ClientsSelect.vue';
import TextInput from '@/components/form-controls/base/TextInput.vue';
import MaterialIconButton from '@/components/form-controls/MaterialIconButton.vue';
import ClientAddEditFormModal from '@/components/modal/ClientAddEditFormModal.vue';
import 'mosha-vue-toastify/dist/style.css';
import { createToast } from 'mosha-vue-toastify';

export default defineComponent({
  name: 'VendorsSearch',
  components: {
    LocationInput,
    MaterialsSelect,
    VendorListing,
    NumberInput,
    CheckboxInput,
    NumberWithMetricInput,
    MaterialIcon,
    FilterableSelect,
    ClientsSelect,
    MaterialIconButton,
    TextInput,
    ClientAddEditFormModal
  },
  setup() {
    const { useDirections, useMap, useMarkers, useOriginMarker } = useGoogleMaps();
    const { createDirectionsRenderer, removeAllRenderers } = useDirections();
    const { createMarker, removeAllMarkers } = useMarkers();
    const { originLatLng, setOriginMarker, removeOriginMarker } = useOriginMarker();
    const [map] = useMap();
    const store = useStore();
    const material = ref<Material>();
    const quantity = ref<NumberWithMetric<VolumeAndWeightUnits>>({ number: 1, metric: VolumeAndWeightUnits.Tons });
    const isInTons = ref(true);
    const routes = ref<(Route | null)[]>([]);
    const trucks = computed(() => store.getters.trucks.get);
    const appDefaults = computed(() => store.getters.appDefaults.get[0] ?? formDefaults.appDefaults);
    const localAppDefaultsState = ref(cloneDeep(appDefaults.value));
    const highlightDiv = ref('');
    const localTrucksState = ref<
      Record<string, Pick<Truck, 'runRate' | 'unloadLoadTime' | 'enabled' | 'capacity' | 'weight' | 'name'>>
    >({});
    const hasShownValidationToast = ref(false);

    const jobName = useSharedInternalState('jobName');
    const client = useSharedInternalState('linkedClientSelect');
    const clientIsEmpty = computed(() => isEqual(client.value, emptyValues.client));
    const clientIsTaxExempt = ref(client.value.taxExempt);
    const [selectedAddEditClient, isAddEditModalOpen, openAddEditModal] = useModalState<Client>();
    const selectSubmittedClient = useSelectSubmittedClient(client);

    const checkAnyTrucksEnabled = () => {
      const anyTrucksEnabledFlag = Object.values(localTrucksState.value).some(truck => truck.enabled);
      return anyTrucksEnabledFlag;
    };

    const setLocalTrucksState = (reset = false) => {
      trucks.value.forEach(truck => {
        if (!reset && Object.getOwnPropertyNames(localTrucksState.value).includes(truck.name)) return;

        localTrucksState.value[truck.name] = {
          runRate: Number(truck.runRate),
          unloadLoadTime: Number(truck.unloadLoadTime),
          enabled: Boolean(truck.enabled) ?? true,
          weight: Number(truck.weight === undefined ? 1 : truck.weight),
          capacity: Number(truck.capacity),
          name: truck.name
        };
      });
    };

    const validateTrucksEnabled = () => {
      if (!checkAnyTrucksEnabled() && !hasShownValidationToast.value) {
        createToast(
          {
            title: 'Must have at least one truck enabled!',
            description: `Please make sure one truck is enabled in Truck Settings.`
          },
          {
            position: 'top-center',
            type: 'danger',
            showCloseButton: true,
            timeout: 1000000,
            transition: 'slide',
            showIcon: true,
            hideProgressBar: true
          }
        );
        hasShownValidationToast.value = true;
      }
    };

    const pointLocation = useSharedInternalState('linkedLocation');
    const rawPointLocation = toRaw(pointLocation.value);

    let location = ref<Location>();
    if (
      pointLocation.value.latitude !== undefined &&
      pointLocation.value.longitude !== undefined &&
      pointLocation.value.latitude !== 0 &&
      pointLocation.value.longitude !== 0
    ) {
      location = ref<Location>({
        geoPoint: rawPointLocation,
        address: ''
      });
      setLocalTrucksState();

      nextTick(() => {
        setOriginMarker(geoPointToLatLng(rawPointLocation));
      });
    }

    watch(appDefaults, newAppDefaults => {
      localAppDefaultsState.value = {
        ...cloneDeep(newAppDefaults),
        minTime: newAppDefaults.minTime !== undefined ? newAppDefaults.minTime : 0
      };
    });

    watch(client, newClient => {
      clientIsTaxExempt.value = newClient.taxExempt;
    });

    const enumerableDistanceUnits = createEnumerableEnum(DistanceUnits);
    const enumerableVolumeAndWeightUnits = createEnumerableEnum(VolumeAndWeightUnits);
    const vendorListings = ref<HTMLDivElement>();
    const sortBy = ref<string>('lowestPrice');

    const normalizedTrucks = computed(() => trucks.value.map(truck => ({ ...truck, weight: truck.weight ?? 1 })));

    const sortedTrucks = computed(() =>
      normalizedTrucks.value.filter(truck => truck.enabled ?? true).sort((a, b) => a.capacity - b.capacity)
    );

    const sortByOptions = computed<Option<string>[]>(() => {
      const localTrucks = Object.values(localTrucksState.value);

      const sortedAndFilteredTrucks = localTrucks
        .filter(truck => truck.enabled ?? true)
        .sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        })
        .map(({ name }) => ({ label: name, value: name }));

      return [
        {
          label: 'Lowest Price',
          value: 'lowestPrice'
        },
        {
          label: 'A - Z',
          value: 'alphaAscending'
        },
        {
          label: 'Z - A',
          value: 'alphaDescending'
        },
        {
          label: 'Time',
          value: 'time'
        },
        ...sortedAndFilteredTrucks
      ];
    });

    const vendors = computed(() => store.getters.vendors.get);

    const normalizedVendors = computed(() =>
      vendors.value.map(vendor => ({
        ...vendor,
        enabled: vendor.enabled !== undefined && vendor.enabled !== null ? vendor.enabled : true
      }))
    );

    const matchedVendors = computed(() =>
      normalizedVendors.value.filter(vendor =>
        vendor.materials.some(stock => stock.material.name === material.value?.name && stock.inStock)
      )
    );

    // Sets markers and polylines on the map.
    watchEffect(async () => {
      if (!location.value && material.value) {
        createToast(
          {
            title: 'Please enter a location!',
            description: `The Location field is missing. Please enter a location to continue.`
          },
          {
            position: 'top-center',
            transition: 'slide',
            type: 'warning',
            showIcon: true,
            timeout: 10000
          }
        );
        highlightDiv.value = 'location';
        return;
      }
      if (location.value && !material.value) {
        createToast(
          {
            title: 'Please select a material!',
            description: `The Material field is missing. Please select a material to continue.`
          },
          {
            position: 'top-center',
            transition: 'slide',
            type: 'warning',
            showIcon: true,
            timeout: 10000
          }
        );
        highlightDiv.value = 'material';
        return;
      }
      highlightDiv.value = '';

      if (!(location.value && material.value)) return;
      const stopLoading = useIsLoadingWithDelay();
      const searchedVendors = await getVendorDirectionsWithinSearchRadius(
        matchedVendors.value,
        location.value.geoPoint,
        localAppDefaultsState.value.searchRadius
      );
      routes.value = searchedVendors?.filter(Boolean);
      routes.value = searchedVendors?.filter(route => route?.vendor.enabled ?? true);
      removeAllRenderers();
      removeAllMarkers();
      routes.value?.forEach((route, index) => {
        if (!route?.directionsResult || !map.value) return;

        const [directionsRenderer] = createDirectionsRenderer(map.value);
        directionsRenderer.setDirections(route.directionsResult);
        directionsRenderer.setOptions({
          suppressMarkers: true,
          suppressInfoWindows: true,
          preserveViewport: true,
          polylineOptions: {
            strokeColor: route.color
          }
        });

        google.maps.event.addListener(directionsRenderer, 'directions_changed', () => {
          const newDirections = directionsRenderer.getDirections();
          if (newDirections && routes.value && routes.value[index]) {
            routes.value[index]!.directionsResult = newDirections;
          }
        });

        const overviewPath = route.directionsResult.routes[0].overview_path;
        createMarker(
          {
            position: overviewPath[overviewPath.length - 1],
            title: route.vendor.name,
            visible: true
          },
          {
            fillColor: route.color
          }
        );
      });

      originLatLng.value && map.value?.setCenter(originLatLng.value);
      stopLoading();
    });

    const noTrucksEnabled = () => {
      createToast(
        {
          title: 'No trucks enabled!',
          description: `Please make sure one truck is enabled in Truck Settings.`
        },
        {
          position: 'top-center',
          type: 'danger',
          showCloseButton: true,
          timeout: 1000000,
          transition: 'slide',
          showIcon: true,
          hideProgressBar: true
        }
      );
    };

    const sortedRoutes = computed(() =>
      routes.value
        .map(route => {
          if (!route?.vendor.enabled) {
            return null;
          }
          if (hasShownValidationToast.value) {
            noTrucksEnabled();
            return null;
          }
          const vendorTruckCosts = sortedTrucks.value
            .filter(truck => localTrucksState.value[truck.name]?.enabled ?? truck.enabled)
            .map(truck => {
              const truckWithPossibleCustomData: Truck = {
                ...truck,
                runRate: localTrucksState.value?.[truck.name]?.runRate ?? truck.runRate,
                unloadLoadTime: localTrucksState.value?.[truck.name]?.unloadLoadTime ?? truck.unloadLoadTime,
                weight: localTrucksState.value?.[truck.name]?.weight === undefined ? 1 : truck.weight,
                capacity: localTrucksState.value?.[truck.name]?.capacity ?? truck.capacity
              };

              const selectedMaterialStock = computed(() => {
                return route?.vendor.materials.find(stock => stock.material.name === material.value?.name);
              });

              const routeCostWithTruck = calculateRouteCostWithTruck({
                truck: truckWithPossibleCustomData,
                selectedMaterialStock: selectedMaterialStock.value,
                route,
                taxExempt: clientIsTaxExempt.value,
                // TODO: Use localized tax percentages.
                // For now we will hardcode in Michigan's state tax.
                tax: 0.06,
                doApplyMarkup: route?.vendor.doApplyMarkup,
                markup: localAppDefaultsState.value.markup,
                quantity: quantity.value.number,
                quantityMetric: quantity.value.metric,
                minTime: localAppDefaultsState.value.minTime
              });

              return {
                truck,
                ...routeCostWithTruck
              };
            });

          return {
            route,
            vendorTruckCosts
          };
        })
        .filter(route => route)
        .slice()
        .sort((a, b) => {
          if (!a?.route || !b?.route) return 0;
          const aName = a.route.vendor.name;
          const bName = b.route.vendor.name;
          const aRouteDuration = getRouteDuration(a.route.directionsResult as google.maps.DirectionsResult);
          const bRouteDuration = getRouteDuration(b.route.directionsResult as google.maps.DirectionsResult);
          const aTruckToSortBy = a.vendorTruckCosts.find(vendorTruckCost => vendorTruckCost?.truck.name === sortBy.value);
          const bTruckToSortBy = b.vendorTruckCosts.find(vendorTruckCost => vendorTruckCost?.truck.name === sortBy.value);

          const aMinTruckCost = Math.min(
            ...a.vendorTruckCosts.map(vendorTruckCost => vendorTruckCost.calculationTooltip.totalCost.value)
          );
          const bMinTruckCost = Math.min(
            ...b.vendorTruckCosts.map(vendorTruckCost => vendorTruckCost.calculationTooltip.totalCost.value)
          );

          switch (sortBy.value) {
            case 'lowestPrice':
              if (!aMinTruckCost || !bMinTruckCost) return 0;
              if (aMinTruckCost < bMinTruckCost) return -1;
              if (bMinTruckCost < aMinTruckCost) return 1;
              return 0;
            case 'alphaAscending':
              if (aName < bName) return -1;
              if (bName < aName) return 1;
              return 0;
            case 'alphaDescending':
              if (aName > bName) return -1;
              if (bName > aName) return 1;
              return 0;
            case 'time':
              if (aRouteDuration < bRouteDuration) return -1;
              if (bRouteDuration < aRouteDuration) return 1;
              return 0;
            default:
              if (!aTruckToSortBy || !bTruckToSortBy) return 0;
              if (aTruckToSortBy.finalRate < bTruckToSortBy.finalRate) return -1;
              if (bTruckToSortBy.finalRate < aTruckToSortBy.finalRate) return 1;
              return 0;
          }
        })
        .map(route => route?.route)
    );

    watch(routes, () => {
      nextTick(() =>
        vendorListings.value?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      );
    });

    // Settings dropdown
    const isSettingsOpen = ref(false);
    const toggleSettingsOpenState = () => {
      isSettingsOpen.value = !isSettingsOpen.value;
    };

    const ToggleInTonsState = (metric: VolumeAndWeightUnits) => {
      isInTons.value = metric === VolumeAndWeightUnits.Tons;
    };

    watch(
      () => quantity.value.metric,
      newMetric => {
        ToggleInTonsState(newMetric);
      }
    );

    watch(
      () => quantity.value.number,
      newNumber => {
        if (!newNumber) return;

        if (newNumber < 1) {
          quantity.value.number = 1;
        }
      }
    );

    const setLocalAppDefaultsState = () => {
      localAppDefaultsState.value = {
        ...cloneDeep(appDefaults.value),
        minTime: appDefaults.value.minTime !== undefined ? appDefaults.value.minTime : 0
      };
    };

    const setSettingsSummary = computed(() => {
      const trucksIncluded = trucks.value.filter(truck => localTrucksState.value[truck.name]?.enabled);

      const summaryPart1 =
        `${clientIsTaxExempt.value ? 'Tax Excluded, ' : 'Tax Included, '}` +
        `$${localAppDefaultsState.value.markup} / ${quantity.value.metric} Mark Up, ` +
        `${localAppDefaultsState.value.minTime} minute min total time`;

      const summaryPart2 = trucksIncluded.map(truck => truck.name).join(', ');

      return {
        part1: summaryPart1,
        part2: summaryPart2
      };
    });

    watch(
      () => Object.values(localTrucksState.value).some(truck => truck.enabled),
      hasEnabledTruck => {
        if (hasEnabledTruck) {
          hasShownValidationToast.value = false;
        }
      }
    );

    watch(
      trucks,
      () => {
        setLocalTrucksState();
        if (trucks.value.length > 0) {
          validateTrucksEnabled();
        }
      },
      { immediate: true }
    );

    onMounted(() => {
      removeOriginMarker;
      setLocalAppDefaultsState();
      setLocalTrucksState();
    });

    const checkEnabledTrucks = (localTruck: Truck) => {
      if (!checkAnyTrucksEnabled()) {
        createToast(
          {
            title: 'Must have at least one truck enabled!',
            description: `Please make sure one truck is enabled in Truck Settings.`
          },
          {
            position: 'top-center',
            type: 'danger',
            showCloseButton: true,
            timeout: 1000000,
            transition: 'slide',
            showIcon: true,
            hideProgressBar: true
          }
        );

        localTrucksState.value[localTruck.name].enabled = true;
        hasShownValidationToast.value = false;
      }
    };

    const goToAppDefaults = () => router.push('/settings/defaults');
    const resetToDefaults = () => {
      setLocalAppDefaultsState();
      setLocalTrucksState(true);
      quantity.value = {
        number: 1,
        metric: VolumeAndWeightUnits.Tons
      };
    };

    return {
      localAppDefaultsState,
      location,
      material,
      routes,
      matchedVendors,
      enumerableDistanceUnits,
      isSettingsOpen,
      toggleSettingsOpenState,
      goToAppDefaults,
      quantity,
      enumerableVolumeAndWeightUnits,
      trucks,
      localTrucksState,
      resetToDefaults,
      sortBy,
      sortByOptions,
      sortedRoutes,
      selectedAddEditClient,
      isAddEditModalOpen,
      openAddEditModal,
      selectSubmittedClient,
      client,
      clientIsEmpty,
      clientIsTaxExempt,
      jobName,
      vendorListings,
      sortedTrucks,
      isInTons,
      setSettingsSummary,
      highlightDiv,
      checkEnabledTrucks
    };
  }
});
